import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { USER_ROLES } from '@/lib/constants/roles';
import { usePermission } from '@/lib/permissions/use-permission';
import { useDeleteSmsWebhook, useSmsWebhookList } from '../api';
import { JsonViewerDialog } from '../components/JsonViewerDialog';
import type { SmsWebhookRow } from '../types';

interface ViewModal {
  title: string;
  content: string;
}

/** Wire values can arrive as string / object / null — normalise to text. */
function asText(v: unknown): string {
  if (v == null) return '';
  return typeof v === 'string' ? v : JSON.stringify(v);
}

export function SmsWebhookListPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const isSAdmin = role === USER_ROLES.S_ADMIN;
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const canAddAcl = usePermission('Manage Campaign.SMS Webhook List', BASE_PERMISSION.ADD);
  const canEditAcl = usePermission('Manage Campaign.SMS Webhook List', BASE_PERMISSION.EDIT);
  const canDeleteAcl = usePermission('Manage Campaign.SMS Webhook List', BASE_PERMISSION.DELETE);
  // Super-admin still can't manage SMS webhooks (unchanged rule). Sub-super-admin
  // is let through only with the matching ACL grant instead of a blanket block.
  const canAdd = !isSAdmin && canAddAcl;
  const canEdit = !isSAdmin && canEditAcl;
  const canDelete = !isSAdmin && canDeleteAcl;
  const query = useSmsWebhookList();
  const del = useDeleteSmsWebhook();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<SmsWebhookRow | null>(null);
  const [modal, setModal] = useState<ViewModal | null>(null);

  const columns = useMemo<ColumnDef<SmsWebhookRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      // User ID + Username are only returned by the API for super-admin and
      // sub-super-admin, so only those panels get the columns.
      ...(isSubOrSuper
        ? ([
            { accessorKey: 'userId', header: 'User ID', size: 100 },
            { accessorKey: 'username', header: 'Username', size: 140 },
          ] as ColumnDef<SmsWebhookRow>[])
        : []),
      {
        accessorKey: 'webhookName',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/update-sms-webhooks/${row.original.id}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.webhookName}
          </Link>
        ),
      },
      {
        accessorKey: 'requestType',
        header: 'Method',
        size: 100,
        cell: ({ row }) => (
          <Badge variant="outline" className="font-mono">
            {row.original.requestType || '—'}
          </Badge>
        ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }) => (
          <span className="break-all font-mono text-xs">{row.original.url}</span>
        ),
      },
      {
        id: 'headerView',
        header: 'Headers',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => {
          const headerText = asText(row.original.header);
          return headerText.trim() ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setModal({ title: 'Headers', content: headerText })}
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        accessorKey: 'timestamp',
        header: 'Request Date',
        size: 170,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.timestamp ?? '—'}
          </span>
        ),
      },
      {
        id: 'bodyView',
        header: 'Body',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => {
          const isPost = row.original.requestType === 'POST';
          const payloadText = asText(row.original.payload);
          const smsTextValue = asText(row.original.smsText);
          // Prefer the field that matches the method; fall back to the other
          // one so rows saved with a mismatched method still show their body.
          const primary = isPost ? payloadText : smsTextValue;
          const content = primary.trim() ? primary : isPost ? smsTextValue : payloadText;
          const showsPayload = primary.trim() ? isPost : !isPost;
          return content.trim() ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setModal({
                  title: showsPayload ? 'Payload' : 'SMS text',
                  content,
                })
              }
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      // Edit + Delete are gated to non-SADMIN roles — super admins and
      // sub-super-admins can browse SMS webhooks but cannot mutate them
      // (they're customer-owned configs). Drop the column entirely so the
      // table doesn't leave a stranded "Settings" header.
      ...(!canEdit && !canDelete
        ? []
        : [
            {
              id: 'actions',
              header: 'Settings',
              size: 90,
              enableHiding: false,
              enableSorting: false,
              cell: ({ row }: { row: { original: SmsWebhookRow } }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label="Row actions"
                    >
                      <Settings2 className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>
                      {row.original.webhookName}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {canEdit && (
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(`/update-sms-webhooks/${row.original.id}`)
                        }
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <DropdownMenuItem
                        onClick={() => setPendingDelete(row.original)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } as ColumnDef<SmsWebhookRow>,
          ]),
    ],
    [navigate, canEdit, canDelete, isSubOrSuper],
  );

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'SMS webhook deleted');
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the webhook.'),
    });
  };

  const canViewList = usePermission('Manage Campaign.SMS Webhook List', BASE_PERMISSION.VIEW);

  if (!canViewList) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="SMS Webhooks"
        description="Outbound HTTP calls used to push SMS via your gateway."
        actions={
          canAdd && (
            <Button asChild>
              <Link to="/add-sms-webhooks">
                <Plus className="h-4 w-4" />
                Add SMS webhook
              </Link>
            </Button>
          )
        }
      />

      <DataTable<SmsWebhookRow, unknown>
        tableId="webhooks:sms:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search SMS webhooks…"
        emptyTitle="No SMS webhooks yet"
        emptyDescription="Add a webhook to wire up an SMS gateway."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={canEdit || canDelete ? { right: ['actions'] } : undefined}
        exportFilename="sms-webhooks.csv"
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            SMS webhook{' '}
            <span className="font-medium text-foreground">{pendingDelete?.webhookName}</span>{' '}
            will be permanently deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />

      <JsonViewerDialog
        open={modal !== null}
        onOpenChange={(o) => !o && setModal(null)}
        title={modal?.title ?? ''}
        content={modal?.content ?? ''}
      />
    </FillPage>
  );
}
